import { toast } from "sonner";
import { offlineMutate, offlineQualifier } from "@/lib/offlineToast";
import { usePaymentHistory } from "@/hooks/usePaymentHistory";
import { type PaymentHistory } from "@/repositories/interfaces";

interface QuickPaymentOptions {
  subscription_id: string;
  payment_date?: string;
  amount?: number;
  currency?: string;
  notes?: string;
  showToast?: boolean;
}

/**
 * Hook to centralize "quick payment" creation logic used across the UI.
 * Returns a helper that creates a payment record with sensible defaults and
 * handles toast notifications on success/failure (toggleable via showToast).
 */
export const useQuickPayment = () => {
  const { createPayment } = usePaymentHistory();

  const createQuickPayment = async ({
    subscription_id,
    payment_date,
    amount = 0,
    currency = "EUR",
    notes = "",
    showToast = true,
  }: QuickPaymentOptions) => {
    const today = new Date().toISOString().split("T")[0];
    const payload: Omit<PaymentHistory, "id" | "created_at"> = {
      subscription_id,
      payment_date: payment_date ?? today,
      amount,
      currency,
      status: "paid" as PaymentHistory["status"],
      reconciliation_source:
        "manual" as PaymentHistory["reconciliation_source"],
      notes: notes || "Quick payment",
    };

    try {
      const result = await offlineMutate(createPayment, payload);
      if (showToast) {
        toast.success("Payment added", { description: offlineQualifier("Payment recorded successfully.") });
      }
      return result;
    } catch (error) {
      console.error("Failed to create quick payment:", error);
      if (showToast) {
        toast.success("Failed to add payment", { description: "An error occurred while recording the payment." });
      }
      throw error;
    }
  };

  return { createQuickPayment };
};
